import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, throwError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { GameOptions, GameSession, GameSessionOptions, GameSessionUser, SessionResultsTable } from '../models/game.model';

@Injectable({
  providedIn: 'root'
})
export class GameSessionService {
  private apiUrl = `${environment.apiUrl}/game-sessions`;

  constructor(private http: HttpClient) { }

  createGameSession(gameId: number, lang: string = 'en'): Observable<GameSession> {
    return this.http.post<GameSession>(this.apiUrl, { gameId, lang });
  }

  getSession(id: number): Observable<GameSession> {
    return this.http.get<GameSession>(`${this.apiUrl}/${id}`);
  }

  joinSession(accessCode: string): Observable<GameSession> {
    return this.http.post<GameSession>(
      `${this.apiUrl}/join`,
      { accessCode }
    );
  }

  joinAsViewer(accessCode: string): Observable<GameSession> {
    return this.http.post<GameSession>(
      `${this.apiUrl}/join-as-viewer`,
      { accessCode }
    );
  }

  updateTeamSize(sessionId: number, teamSize: number): Observable<GameSession> {
    return this.http.patch<GameSession>(
      `${this.apiUrl}/${sessionId}/team-size`,
      { teamSize }
    );
  }

  markReady(sessionId: number, ready: boolean = true): Observable<{ message: string; sessionStatus: string }> {
    return this.http.post<{ message: string; sessionStatus: string }>(
      `${this.apiUrl}/${sessionId}/ready`,
      { ready }
    );
  }

  removePlayer(sessionId: number, userId: number): Observable<GameSession> {
    return this.http.delete<GameSession>(`${this.apiUrl}/${sessionId}/participants/${userId}`);
  }

  leaveSession(sessionId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${sessionId}/leave`);
  }

  deleteSession(sessionId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${sessionId}`);
  }

  getCurrentSession(): Observable<GameSession | null> {
    return this.http.get<GameSession>(`${this.apiUrl}/current`)
      .pipe(
        catchError(error => {
          if (error.status === 404) {
            return of(null);
          }
          return throwError(() => error);
        })
      );
  }

  assignTeamToSessionUser(sessionId: number, sessionUserId: number, teamId: number): Observable<GameSessionUser> {
    return this.http.patch<GameSessionUser>(`${this.apiUrl}/${sessionId}/user/${sessionUserId}/session-user-team`, {
      teamId
    });
  }

  createTeamAndAssignToSession(sessionId: number, sessionUserId: number, name: string): Observable<GameSessionUser> {
    return this.http.post<GameSessionUser>(`${this.apiUrl}/${sessionId}/user/${sessionUserId}/create-team-and-assign`, {
      name
    });
  }

  updateSessionUserColor(sessionId: number, sessionUserId: number, color: string): Observable<GameSessionUser> {
    return this.http.patch<GameSessionUser>(`${this.apiUrl}/${sessionId}/user/${sessionUserId}/session-user-color`, { color });
  }

  updateSessionSettings(sessionId: number, settings: GameSessionOptions): Observable<GameSession> {
    return this.http.patch<GameSession>(`${this.apiUrl}/${sessionId}/settings`, {
      settings: JSON.stringify(settings)
    });
  }

  updateSessionLanguage(sessionId: number, lang: string): Observable<GameSession> {
    return this.http.patch<GameSession>(`${this.apiUrl}/${sessionId}/lang`, {
      lang
    });
  }

  startGame(sessionId: number): Observable<GameSession> {
    return this.http.post<GameSession>(`${this.apiUrl}/${sessionId}/start`, {});
  }

  submitRoundAnswer(sessionId: number, sessionTeamId: number, answer: string): Observable<GameSession> {
    return this.http.post<GameSession>(`${this.apiUrl}/${sessionId}/team/${sessionTeamId}/answer`, { answer });
  }

  updateRoundAnswer(sessionId: number, sessionTeamId: number, answer: string): Observable<GameSession> {
    return this.http.patch<GameSession>(`${this.apiUrl}/${sessionId}/team/${sessionTeamId}/answer`, { answer });
  }

  readyForResults(sessionId: number, sessionTeamId: number, ready: boolean = true, skipResults = true): Observable<GameSession> {
    return this.http.post<GameSession>(`${this.apiUrl}/${sessionId}/team/${sessionTeamId}/ready-for-results`, { ready, skipResults });
  }

  readyToComplete(sessionId: number, sessionTeamId: number, ready: boolean = true): Observable<GameSession> {
    return this.http.post<GameSession>(`${this.apiUrl}/${sessionId}/team/${sessionTeamId}/ready-to-complete`, { ready });
  }

  nextRound(sessionId: number): Observable<GameSession> {
    return this.http.post<GameSession>(`${this.apiUrl}/${sessionId}/next-round`, {});
  }

  approveTeamAnswer(sessionId: number, sessionTeamId: number, targetTeamId: number, approved: boolean = true): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${sessionId}/team/${sessionTeamId}/approve-team-answer`, { targetTeamId, approved });
  }

  getStageResults(sessionId: number): Observable<SessionResultsTable> {
    return this.http.get<SessionResultsTable>(`${this.apiUrl}/${sessionId}/stage-results`);
  }
}
